/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react';
import {Row, FormGroup, Label, Col, Input, Button } from 'reactstrap'

import ReactTable from 'react-table-legacy'
import "react-table-legacy/react-table.css";

import DropDownTreeSelect from 'react-dropdown-tree-select-sco'
import '../css/AsterionDB.css'

import { css } from '@emotion/react';
import { RingLoader } from 'react-spinners'

import {AppContext} from '../AppContext';

class PathParser extends React.Component
{
  uniquePathKey = 'pathParser.uniquePathColumns';
  elementKey = 'pathParser.elementColumns';

  uniqueColumnWidths = {};
  elementColumnWidths = {};

  debouncedSaveUniqueColumnWidths = null;
  debouncedSaveElementColumnWidths = null;

  elementTree = null;
  maximumDepth = 0;
  maskDepth = 0;

  selectedKeywords = [];
  filenameKeyword = null;

  buildPathElements(maskSearchString)
  {
    let pathElements = [];

    let depth = this.maximumDepth;
    if (maskSearchString) depth = this.maximumDepth - this.maskDepth;
    
    for ( let x = 0; x < depth; x++)
      pathElements[x] = {elementPosition: 'Subdirectory #' + (x + 1)}

    return {pathElements: pathElements}
  }

  componentDidMount()
  {
    this.uniqueColumnWidths = this.context.appLocalStorage.getColumnWidths(this.state.uniquePathColumns, this.uniquePathKey);
    this.elementColumnWidths = this.context.appLocalStorage.getColumnWidths(this.state.elementColumns, this.elementKey);

    document.title = this.context.websiteProfile.profileName + ': Path Parser'
  }

  constructor(props)
  {
    super();

    let uniquePathColumns = 
    [
      { Header: 'Directory Path', id: 'uniquePath', accessor: d => {return d.uniquePath}, Cell: row => <div><span title={row.value}>{row.value}</span></div>},
      { Header: 'Parsed Values', id: 'parsedValues', accessor: d => {return d.parsedValues}, Cell: row => <div><span title={row.value}>{row.value}</span></div>},
      { Header: 'Depth', id: 'pathDepth', accessor: d => {return d.depth}, show: false}
    ];

    let elementColumns =
    [
      { Header: 'Subdirectory', id: 'elementPosition', accessor: d => {return d.elementPosition}},
      { Header: 'Keyword', id: 'keyword',
        Cell: (rowInfo) =>
        {
          if (this.state.keywordArray.length)
            return(
              <DropDownTreeSelect data={this.state.keywordArray[rowInfo.index]} className={'bootstrap-demo'} selectChildrenOnly={true} mode={'radioSelect'}
              onChange={this.onSubDirKeywordChangeHandler.bind(this, rowInfo.index)} />
            );
          else
            return(null);
        }
      }
    ];

    this.state =
    {
      searchString: '',
      uniquePaths: [],
      pathElements: [],
      objectCount: 0,
      uniquePathColumns: uniquePathColumns,
      elementColumns: elementColumns,
      searchDisabled: true,
      queryKeywords: [],
      viewType: 'uncataloged',
      spinnerIsSpinning: false,
      maskSearchString: true,
      keywordArray: [],
      keywordGroups: []
    }

    this.debouncedSaveElementColumnWidths = window.debounce(this.saveElementColumnWidths, 500);
    this.debouncedSaveUniqueColumnWidths = window.debounce(this.saveUniqueColumnWidths, 500);

    this.onKeyDownSearchString = this.onKeyDownSearchString.bind(this);
    this.searchSourcePath = this.searchSourcePath.bind(this);
    this.setSearchString = this.setSearchString.bind(this);
    this.parsePaths = this.parsePaths.bind(this);
    this.viewTypeSelectorChanged = this.viewTypeSelectorChanged.bind(this);
    this.toggleMaskSearchString = this.toggleMaskSearchString.bind(this);
    this.onFilenameKeywordChangeHandler = this.onFilenameKeywordChangeHandler.bind(this);
  }

  determineMaximumPathDepth(paths)
  {
    let maximumDepth = 0;
    let windowsPaths = 0;
    let linuxPaths = 0;

    let sourcePaths = [];
    for (let x = 0; x < paths.length; x++)
    {
      let depth = 1;
      let type = 'linux';
      if ('/' === paths[x][0])
      {
        linuxPaths++;

        let splits = paths[x].split('/');
        depth = splits.length - 1;
      }
      else if ('\\' === paths[x][0] || ':' === paths[x][1])
      {
        type = 'windows';
        windowsPaths++;

        let splits = paths[x].split('\\');
        depth = splits.length - 2;

        if (maximumDepth < depth) maximumDepth = depth;
      }

      sourcePaths[x] = {depth: depth, type: type, sourcePath: paths[x]};

      if (maximumDepth < depth) maximumDepth = depth;
    }

    this.maskDepth = 0;

    if ('/' === this.state.searchString[0])
      this.maskDepth = this.state.searchString.split('/').length - 2;
    
    if ('\\' === this.state.searchString[0])
      this.maskDepth = this.state.searchString.split('\\').length - 3;
    
    this.maximumDepth =  maximumDepth;
    
    if (windowsPaths && linuxPaths)
    {
      this.context.openAppModal('Mixed paths...', 'The search results contains both Windows and Linux paths.');
    }

    return sourcePaths;
  }

  async fetchQueryKeywords(objectTypeIds, pathElementsLength)
  {
    var bodyData = {objectTypeIds: objectTypeIds};

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getAssignableKeywords', bodyData);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error getting keywords');

    let queryKeywords = result.jsonData.assignableKeywords;
    let keywordGroups = [];
    let lastKeywordGroup = null;
    if (undefined !== queryKeywords)
    {
      for (let x = 0; x < queryKeywords.length; x++)
      {
        if (lastKeywordGroup !== queryKeywords[x].keywordGroup)
        {
          lastKeywordGroup = queryKeywords[x].keywordGroup;
          let keywordGroup = { label: queryKeywords[x].keywordGroup, value: '', children: []};
          keywordGroups.push(keywordGroup);
        }

        let keyword = 
        { 
          label: queryKeywords[x].keyword, 
          value: queryKeywords[x],
          tagLabel: queryKeywords[x].keywordGroup + ' - ' + queryKeywords[x].keyword,
          checked: false
        }
        keywordGroups[keywordGroups.length-1].children.push(keyword);
      }
    }
  
    let keywordArray = [];
    this.selectedKeywords = [];
    for (let x = 0; x < pathElementsLength; x++)
    {
      this.selectedKeywords.push(null);
      keywordArray.push(structuredClone(keywordGroups));
    }

    this.setState({keywordArray, keywordGroups});    
  }

  viewTypeSelectorChanged(event)
  {
    this.setState({viewType: event.target.value});
  }

  onFilenameKeywordChangeHandler(selectedValue)
  {
    this.filenameKeyword = selectedValue.value.keywordId;
    let keywordGroups = this.state.keywordGroups;
    let found = false;

    for (let x = 0; x < keywordGroups.length; x++)
    {
      for (let y = 0; y < keywordGroups[x].children.length; y++)
      {
        if (keywordGroups[x].children[y].value.keywordId === selectedValue.value.keywordId)
        {
          keywordGroups[x].children[y].checked = true;
          found = true;
          break;
        }
      }
      if (found) break;
    }

    this.setState({keywordGroups});
  }

  onKeyDownSearchString(event)
  {
    if (window.enterKeyPressed(event.keyCode))
    {
      if ('' === this.state.searchString)
      {
        this.context.postNotification('warning', 'You must set at least one parsing rule.');        
        return;
      }
      this.searchSourcePath(event);
    } 
  }

  onSubDirKeywordChangeHandler(index, selectedValue, selectedNodes)
  {
    this.selectedKeywords[index] = selectedValue.value.keywordId;

    let keywordArray = this.state.keywordArray;

    let keywordGroup = keywordArray[index];
    let found = false;

    for (let x = 0; x < keywordGroup.length; x++)
    {
      for (let y = 0; y < keywordGroup[x].children.length; y++)
      {
        if (keywordGroup[x].children[y].value.keywordId === selectedValue.value.keywordId)
        {
          keywordArray[index][x].children[y].checked = selectedValue.checked;
        }
        else
          keywordArray[index][x].children[y].checked = false;
      }
    }

    let uniquePaths = this.state.uniquePaths;

    if (index !== this.maximumDepth)
    {
      for (let x = 0; x < uniquePaths.length; x++)
      {
        let parsedValues = '';

        let values = [];
        if ('linux' === uniquePaths[x].type)
          values = uniquePaths[x].uniquePath.split('/');
        else
          values = uniquePaths[x].uniquePath.split('\\');

        for (let y = 0; y < this.selectedKeywords.length; y++)
        {
          if (null !== this.selectedKeywords[y] && undefined !== values[y+1])
          {
            if ('' === parsedValues)
            parsedValues = values[y+1] + ',';
          else
            parsedValues += ' ' + values[y+1] + ',';
          }
        }

        uniquePaths[x].parsedValues = parsedValues.substring(0, parsedValues.length - 1);
      }
    }

    this.setState({keywordArray, uniquePaths: JSON.parse(JSON.stringify(uniquePaths))});
  }

  async parsePaths(event)
  {
    if (!this.selectedKeywords.length)
    {
      this.context.postNotification('warning', 'You must set at least one parsing rule.');
      return;
    }

    event.preventDefault();
    var bodyData = {};

    bodyData.searchString = this.state.searchString;
    bodyData.viewType = this.state.viewType;
    
    let subdirectoryKeywords = [];
    if (this.state.maskSearchString) for (let x = 0; x < this.maskDepth; x++) subdirectoryKeywords[x] = null;

    for (let x = 0; x < this.selectedKeywords.length; x++)
      if (null !== this.selectedKeywords[x]) subdirectoryKeywords[this.state.maskSearchString ? x + this.maskDepth : x] = this.selectedKeywords[x];

    bodyData.subdirectoryKeywords = subdirectoryKeywords;

    bodyData.filenameKeyword = this.filenameKeyword;

    this.setState({spinnerIsSpinning: true});

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'parseSourcePath', bodyData);
    if ('success' !== result.status)
    {
      this.setState({spinnerIsSpinning: false});
      return this.context.dbTwig.apiErrorHandler(result, 'Error parsing source paths');
    } 
    this.setState({spinnerIsSpinning: false});

    if (result.jsonData.rejectedAssignment)
      this.context.postNotification('warning', 'One or more keyword assignment was rejected due to object-type restrictions.');
    else
      this.context.postNotification('info', 'Parsing complete.');
  }

  render()
  {
    let searchStats = this.renderSearchStats();
    let maskCheckBox = this.renderMaskCheckBox();

    const override = css`
      display: block;
      margin: 0 auto;
      border-color: red;
    `;
    
    return(
      <div>
        <div style={{position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)"}}>
          <RingLoader css={override} size={75} color={'#4A90E2'} loading={this.state.spinnerIsSpinning}/>          
        </div>
        <Row style={{paddingTop: "10px"}}><h2>Path to Keyword/Tag Parser</h2></Row>
        <Row style={{paddingTop: "10px"}}>
          <Col xs='12' lg='4' className='mb-3 pull-lg-8'>
            <Row><Col><Label for='searchString' >Search String</Label></Col></Row>
            <Row>
              <Col>
                <Input id='searchString' type='text' placeholder='Enter a search string...' autoFocus={true} onKeyDown={this.onKeyDownSearchString} 
                  value={this.state.searchString} onChange={this.setSearchString} 
                />
              </Col>
            </Row>
            <Row style={{paddingTop: "10px"}}>
              <Col sm={3}>
                <FormGroup check>
                  <Input type="radio" id='uncatRadio' name="viewTypeRadio" onChange={this.viewTypeSelectorChanged} 
                        checked={('uncataloged' === this.state.viewType ? 'checked' : '')} value="uncataloged" />
                  <Label for='uncatRadio'>Uncataloged</Label>
                </FormGroup>
              </Col>
              <Col sm={3}>
                <FormGroup check>
                  <Input type="radio" id='catRadio' name="viewTypeRadio" onChange={this.viewTypeSelectorChanged} 
                        checked={('cataloged' === this.state.viewType ? 'checked' : '')} value="cataloged" />
                  <Label for='catRadio'>Cataloged</Label>
                </FormGroup>
              </Col>
              <Col sm={2}>
                <Button color='primary' onClick={this.searchSourcePath} disabled={this.state.searchDisabled}>Search</Button>
              </Col>
            </Row>            
            {maskCheckBox}              
            <Row style={{paddingTop: "10px"}}>
              <Col>
              { 0 !== this.state.uniquePaths.length &&
                <div>Map subdirectories to keywords</div>
              }
              </Col>
            </Row>
            <Row style={{paddingTop: "10px"}}>
              <Col>
              { 0 !== this.state.uniquePaths.length && 
                <ReactTable data={this.state.pathElements} showPagination={false} columns={this.state.elementColumns} minRows={1} className="-highlight"
                  defaultPageSize={15} onResizedChange={(resizedColumns, event) => this.debouncedSaveElementColumnWidths(resizedColumns, event)} />            
              }
              </Col>
            </Row>
            <Row style={{paddingTop: "10px"}}>
            { 0 !== this.state.uniquePaths.length &&
              <div>
                <Col>Map file name to this keyword</Col>
              </div>
            }
            </Row>
            <Row style={{paddingTop: "10px"}}>
            { 0 !== this.state.uniquePaths.length &&
              <div>
                <Col>                
                  <DropDownTreeSelect data={this.state.keywordGroups} className={'bootstrap-demo'} selectChildrenOnly={true} mode={'radioSelect'}
                    onChange={this.onFilenameKeywordChangeHandler} />
                </Col>
              </div>
            }
            </Row>
            <Row style={{paddingTop: "10px"}}>
              <Col>
              { 0 !== this.state.uniquePaths.length &&
                <Button color='primary' onClick={this.parsePaths} >Parse</Button>
              }
              </Col>
            </Row>
          </Col>
          <Col xs='12' lg='8' className='mb-3 push-lg-4'>
            <Row><Col>{searchStats}</Col></Row>
            <Row style={{paddingTop: "10px"}}>
              <Col>
              { 0 !== this.state.uniquePaths.length && 
                <ReactTable data={this.state.uniquePaths} showPagination={true} columns={this.state.uniquePathColumns} minRows={1} className="-highlight"
                  defaultPageSize={15} pageSizeOptions={[5, 10, 15, 20, 30, 50, 100]}
                  onResizedChange={(resizedColumns, event) => this.debouncedSaveUniqueColumnWidths(resizedColumns, event)} />            
              }
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }

  renderSearchStats()
  {
    if (!this.state.uniquePaths.length) return null;

    return (<div>Unique Paths:&nbsp;{this.state.uniquePaths.length}&nbsp;Object Count:&nbsp;{this.state.objectCount}</div>);
  }

  renderMaskCheckBox()
  {
    if (-1 === this.state.searchString.search('%') && 0 !== this.state.uniquePaths.length)
      return(
        <FormGroup row>
          <Label for='maskSearchString' sm="4" >Mask Search String?</Label>
          <Col sm="1" >
            <Input type="checkbox" id="maskSearchString" onChange={this.toggleMaskSearchString} 
              checked={this.state.maskSearchString ? "checked" : ''} style={{marginLeft: 0, marginTop: '0.7rem'}}/>
          </Col>
        </FormGroup>
      );
    else
      return null;
  }

  async searchSourcePath(event)
  {
    let bodyData = {};

    bodyData.viewType = this.state.viewType;
    bodyData.searchString = this.state.searchString;

    if (-1 !== this.state.searchString.search('%')) this.setState({maskSearchString: false});

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'searchSourcePath', bodyData);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error searching source path');

    if (!result.jsonData.objectCount)
    {
      this.context.postNotification('warning', 'No objects found...!!!');
      return;
    }
    
    let objectCount = result.jsonData.objectCount;
    let sourcePaths = this.determineMaximumPathDepth(result.jsonData.sourcePaths);
    let pathElementsObj = this.buildPathElements(this.state.maskSearchString);

    let uniquePaths = [];

    for (let x = 0; x < sourcePaths.length; x++)
    {
      let uniquePath = null;
      if (this.state.maskSearchString)
      {
        let lastDelim = this.state.searchString.lastIndexOf('/');
        if (-1 === lastDelim) lastDelim = this.state.searchString.lastIndexOf('\\');
        if (-1 === lastDelim)
          uniquePath = sourcePaths[x].sourcePath;
        else
          uniquePath = sourcePaths[x].sourcePath.slice(lastDelim);
      }
      else
        uniquePath = sourcePaths[x].sourcePath;
  
      uniquePaths[x] = {uniquePath: uniquePath, type: sourcePaths[x].type, depth: sourcePaths[x].depth, parsedValues: undefined};
    }

    await this.fetchQueryKeywords(result.jsonData.objectTypeIds, pathElementsObj.pathElements.length);

    this.setState(
    {
      uniquePaths: uniquePaths, 
      objectCount: objectCount,
      pathElements: pathElementsObj.pathElements
    });

  }

  saveUniqueColumnWidths(resizedColumns)
  {
    this.context.appLocalStorage.saveColumnWidths(resizedColumns, this.uniqueColumnWidths, this.uniquePathKey);    
  }

  saveElementColumnWidths(resizedColumns)
  {
    this.context.appLocalStorage.saveColumnWidths(resizedColumns, this.elementColumnWidths, this.elementKey);
  }

  setSearchString(event)
  {
    let disabled = ('' === event.target.value ? true : false);
    if (this.state.pathElements.length) this.setState({pathElements: [], uniquePaths: []});
    this.setState({searchString: event.target.value, searchDisabled: disabled});
  }

  async toggleMaskSearchString()
  {
    this.setState({uniquePaths: []});

    let maskSearchString = !this.state.maskSearchString;
    let uniquePaths = [];

    let pathElementsObj = this.buildPathElements(maskSearchString);

    for (let x = 0; x < this.state.uniquePaths.length; x++)
    {
      uniquePaths[x] = 
      {
        uniquePath: this.state.uniquePaths[x].uniquePath, 
        parsedValues: undefined,
        type: this.state.uniquePaths[x].type
      };

      let delim = ('linux' === uniquePaths[x].type ? '/' : '\\');
      if (maskSearchString)
      {
        let lastDelim = this.state.searchString.lastIndexOf(delim);
        if (-1 !== lastDelim) uniquePaths[x].uniquePath = uniquePaths[x].uniquePath.slice(lastDelim);
      }
      else
      {
        let lastDelim = this.state.searchString.lastIndexOf(delim);
        if (-1 !== lastDelim)
        {
          let searchString = this.state.searchString.substring(0, lastDelim);
          uniquePaths[x].uniquePath = searchString + uniquePaths[x].uniquePath;
        }
      }
    }

    await this.fetchQueryKeywords(this.state.objectTypeIds, pathElementsObj.pathElements.length);

    this.setState(
    {
      maskSearchString: maskSearchString, 
      uniquePaths: uniquePaths, 
      pathElements: pathElementsObj.pathElements
    });
  }
}

PathParser.contextType = AppContext;

export default PathParser